import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectOrganizationId } from 'core/userContext';
import { withRouter, useParams } from 'react-router';
import { PageHeader } from 'ui/pageHeader';
import { Spinner } from 'ui/spinner';
import { AnnotatedSection, Annotation, SectionContent } from 'ui-beta/annotatedSection';
import { PathSection } from './PathSection';
import {
  getIsLoading,
  selectAppSideAssociationForPath,
  selectAssetLinksByOrgIdAndDomain,
} from '../selectors';
import { fetchDeepLinksRequest } from '../actions';
import { Container, EmptyStateContainer } from './MailDomains.styled';

const TEXT = {
  pageHeaderDescription:
    'Iterable combines the destination domain and the paths specified below to determine which links in your email campaigns to rewrite as deep links.',
  pathsTitle: 'Paths',
  pathsText:
    'Upload your apple-app-site-association file (iOS) and assetlinks.json file (Android).',
  appleConfigTitle: 'Apple',
  androidConfigTitle: 'Android',
  configButtonCTA: 'Edit configuration',
  appleConfigLabel: 'apple-app-site-association file',
  androidConfigLabel: 'assetlinks.json',
  emptyConfigLabel: 'None',
  androidHelpText:
    'Even if you only have an Android app, you must still upload an apple-app-site-association file. Iterable uses this file to determine which paths to rewrite as deep links.',
};

export const Path = () => {
  const dispatch = useDispatch();
  const organizationId = useSelector(selectOrganizationId);
  const { trackingDomainName, pathName } = useParams();

  const appleAppSiteAssociationFile = useSelector((state) =>
    selectAppSideAssociationForPath(state, { trackingDomainName, appDomainName: pathName }),
  );
  const assetLinks = useSelector((state) =>
    selectAssetLinksByOrgIdAndDomain(state, { organizationId, trackingDomainName }),
  );
  const isLoading = useSelector(getIsLoading);
  useEffect(() => {
    if (!appleAppSiteAssociationFile) {
      dispatch(fetchDeepLinksRequest());
    }
  }, [appleAppSiteAssociationFile]);

  return (
    <Container>
      <PageHeader
        breadcrumb={{
          content: `Tracking domain - ${decodeURIComponent(trackingDomainName)}`,
          href: `/settings/domains/${trackingDomainName}`,
        }}
      >
        Destination domain - {decodeURIComponent(pathName)}
      </PageHeader>
      {isLoading ? (
        <EmptyStateContainer>
          <Spinner />
        </EmptyStateContainer>
      ) : (
        <AnnotatedSection breakpoint={1000}>
          <Annotation title={TEXT.pathsTitle}>{TEXT.pathsText}</Annotation>
          <SectionContent>
            <PathSection
              appDomain={pathName}
              appleAppSiteAssociationFile={appleAppSiteAssociationFile}
              buttonCTA={TEXT.configButtonCTA}
              configLabel={TEXT.appleConfigLabel}
              configTitle={TEXT.appleConfigTitle}
              domain={trackingDomainName}
              platform="apple"
            />
            <PathSection
              assetLinks={assetLinks}
              appDomain={pathName}
              buttonCTA={TEXT.configButtonCTA}
              configLabel={TEXT.androidConfigLabel}
              configTitle={TEXT.androidConfigTitle}
              configHelpText={TEXT.androidHelpText}
              domain={trackingDomainName}
              platform="android"
            />
          </SectionContent>
        </AnnotatedSection>
      )}
    </Container>
  );
};

export default withRouter(Path);
